import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Stack,
    Box,
    Button,
    TextField,
    LinearProgress,
    Alert,
    Card,
    CardContent,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { globalFormatCurrencyBRL, globalMaskDocument } from "../../../shared/utils";
import type { PoliceEvent } from "../types/Polices";

interface ModalFirePoliceCancelProps {
    open: boolean;
    onClose: () => void;
    data: PoliceEvent | null;
    isLoading?: boolean;
    onConfirm: (id: string, reason: string) => Promise<void>;
}

export const ModalFirePoliceCancel = ({
    open,
    onClose,
    data,
    isLoading = false,
    onConfirm,
}: ModalFirePoliceCancelProps) => {
    const [reason, setReason] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setReason("");
        }
    }, [open]);

    if (!open || !data) return null;

    const handleSubmit = async () => {
        if (!reason.trim()) return;
        if (!data.id) return;

        try {
            setIsSubmitting(true);
            await onConfirm(data.id, reason);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            {/* Header */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" sx={{ fontWeight: 300, color: "primary.main" }}>
                        Cancelar Apólice
                    </Typography>

                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            {(isSubmitting || isLoading) && <LinearProgress />}

            <DialogContent dividers sx={{ pb: 3 }}>
                {/* ALERTA */}
                <Alert severity="warning" sx={{ mb: 3 }}>
                    Para cancelar a apólice, confirme abaixo o motivo do cancelamento.
                </Alert>

                {/* CARD DE IDENTIFICAÇÃO */}
                <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Stack spacing={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Detalhes da Apólice
                            </Typography>

                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Inquilino:</Typography>
                                <Typography fontWeight={600}>
                                    {data?.tenant_name ?? "—"}
                                </Typography>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Documento:</Typography>
                                <Typography fontWeight={600}>
                                    {data?.tenant_document
                                        ? globalMaskDocument(data?.tenant_document)
                                        : "—"}
                                </Typography>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Seguradora:</Typography>
                                <Typography fontWeight={600}>
                                    {data?.insuranceCompany?.name ?? "—"}
                                </Typography>
                            </Stack>

                            <Stack direction="row" justifyContent="space-between">
                                <Typography variant="body2">Prêmio total:</Typography>
                                <Typography fontWeight={600}>
                                    {globalFormatCurrencyBRL(String(data?.insurance_prize ?? 0))}
                                </Typography>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                {/* MOTIVO */}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Motivo do cancelamento *
                </Typography>

                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Descreva o motivo do cancelamento..."
                    error={!reason.trim()}
                    helperText={!reason.trim() ? "O motivo é obrigatório." : ""}
                />
            </DialogContent>

            {/* Actions */}
            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={onClose}
                    color="inherit"
                    variant="contained"
                >
                    Voltar
                </Button>

                <Button
                    variant="contained"
                    color="error"
                    disabled={!reason.trim() || isSubmitting}
                    onClick={handleSubmit}
                >
                    {isSubmitting ? "Cancelando..." : "Confirmar Cancelamento"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
